import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Lock, Download, RefreshCw, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLeads() {
  const [key, setKey] = useState(() => sessionStorage.getItem("mla_admin_key") || "");
  const [leads, setLeads] = useState(null);
  const [count, setCount] = useState(0);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const r = await axios.get(`${API}/admin/leads`, { headers: { "X-Admin-Key": key } });
      setLeads(r.data.leads);
      setCount(r.data.count);
      sessionStorage.setItem("mla_admin_key", key);
    } catch (err) {
      setLeads(null);
      setError(err?.response?.status === 401 ? "Invalid admin key." : "Could not load leads. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = () => {
    if (!leads) return;
    const rows = ["email,first_seen,last_seen"].concat(
      leads.map((l) => `${l.email},${l.created_at || ""},${l.last_seen || ""}`)
    );
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mla-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

  return (
    <main className="max-w-[900px] mx-auto px-5 md:px-10 py-12 relative z-10 min-h-screen" data-testid="admin-leads">
      <Link to="/" className="font-mono-ui text-[11px] uppercase tracking-[0.16em] text-[#1C2430]/55 hover:text-[#1C2430] inline-flex items-center gap-1.5 mb-8">
        <ArrowLeft size={13} /> Back to the guide
      </Link>

      <div className="font-mono-ui text-[10px] uppercase tracking-[0.24em] text-[#2F6F4F] mb-3">Admin</div>
      <h1 className="font-serif-display text-[40px] md:text-[52px] leading-[0.98] font-medium text-[#1C2430] mb-8">
        Sign-ups
      </h1>

      {leads === null ? (
        <form onSubmit={load} className="max-w-md" data-testid="admin-login-form">
          <label className="font-mono-ui text-[10px] uppercase tracking-[0.12em] text-[#1C2430]/55 flex items-center gap-2 mb-2">
            <Lock size={12} /> Admin key
          </label>
          <div className="flex gap-3">
            <input
              type="password"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="Enter admin key"
              data-testid="admin-key-input"
              className="flex-1 font-mono-ui text-[14px] bg-white border border-[var(--border-heavy)] px-4 py-3 focus:outline-none focus:border-[#1C2430]"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-view-btn"
              className="font-mono-ui text-[11px] uppercase tracking-[0.16em] bg-[#1C2430] text-[#FBFBF9] px-6 py-3 hover:bg-[#364152] transition-colors disabled:opacity-60"
            >
              {loading ? "…" : "View"}
            </button>
          </div>
          {error && <p className="mt-3 font-mono-ui text-[12px] text-[#D26040]" data-testid="admin-error">{error}</p>}
        </form>
      ) : (
        <div>
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div className="font-mono-ui text-[13px] text-[#1C2430]/70" data-testid="admin-count">
              <span className="text-[#2F6F4F] font-bold text-[18px]">{count}</span> total sign-up{count === 1 ? "" : "s"}
            </div>
            <div className="flex gap-2">
              <button onClick={() => load()} className="font-mono-ui text-[11px] uppercase tracking-[0.12em] border border-[var(--border-heavy)] px-4 py-2 hover:border-[#1C2430] transition-colors inline-flex items-center gap-1.5" data-testid="admin-refresh">
                <RefreshCw size={12} /> Refresh
              </button>
              <button onClick={exportCsv} className="font-mono-ui text-[11px] uppercase tracking-[0.12em] bg-[#1C2430] text-[#FBFBF9] px-4 py-2 hover:bg-[#364152] transition-colors inline-flex items-center gap-1.5" data-testid="admin-export">
                <Download size={12} /> Export CSV
              </button>
            </div>
          </div>

          {count === 0 ? (
            <p className="text-[15px] text-[#1C2430]/60 border border-[var(--border-light)] p-8 text-center">No sign-ups yet.</p>
          ) : (
            <div className="border border-[var(--border-heavy)] overflow-x-auto">
              <table className="w-full border-collapse text-[13.5px]" data-testid="admin-table">
                <thead>
                  <tr className="bg-[#1C2430] text-[#FBFBF9]">
                    <th className="text-left font-mono-ui text-[10px] uppercase tracking-[0.14em] px-4 py-3 w-10">#</th>
                    <th className="text-left font-mono-ui text-[10px] uppercase tracking-[0.14em] px-4 py-3">Email</th>
                    <th className="text-left font-mono-ui text-[10px] uppercase tracking-[0.14em] px-4 py-3">First seen</th>
                    <th className="text-left font-mono-ui text-[10px] uppercase tracking-[0.14em] px-4 py-3">Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l, i) => (
                    <tr key={l.email} className="border-t border-[var(--border-light)] odd:bg-black/[0.015]">
                      <td className="px-4 py-3 font-mono-ui text-[#1C2430]/40 tabular-nums">{i + 1}</td>
                      <td className="px-4 py-3 text-[#1C2430]">{l.email}</td>
                      <td className="px-4 py-3 font-mono-ui text-[12px] text-[#1C2430]/60">{fmt(l.created_at)}</td>
                      <td className="px-4 py-3 font-mono-ui text-[12px] text-[#1C2430]/60">{fmt(l.last_seen)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </main>
  );
}
